import React, { useState } from "react";
import { FlaskConical, Play, Bug, Trash2 } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { endpoints } from "@/lib/api";
import { Panel, PageHeader, Loading } from "@/components/common";
import { inr, inrL, num } from "@/lib/format";

function Field({ label, value, onChange, unit, placeholder }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label} {unit && <span className="text-slate-400">({unit})</span>}</span>
      <input type="number" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-300" />
    </label>
  );
}

function Delta({ label, base, proj, fmt, lower = true }) {
  const diff = proj - base;
  const good = lower ? diff < 0 : diff > 0;
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-600">{label}</span>
      <div className="flex items-center gap-3 font-mono text-sm">
        <span className="text-slate-400">{fmt(base)}</span>
        <span className="text-slate-300">→</span>
        <span className="font-semibold text-slate-900">{fmt(proj)}</span>
        <span className={`text-xs ${good ? "text-emerald-600" : "text-red-600"}`}>
          {diff > 0 ? "▲" : diff < 0 ? "▼" : "="} {fmt(Math.abs(diff))}
        </span>
      </div>
    </div>
  );
}

export default function SimulationStudio() {
  const { data: cfg, refresh } = usePoll(endpoints.simConfig, 8000);
  const [milk, setMilk] = useState("");
  const [ambient, setAmbient] = useState("");
  const [fuel, setFuel] = useState("");
  const [elec, setElec] = useState("");
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      const payload = {};
      if (milk) payload.milk_klpd = parseFloat(milk);
      if (ambient) payload.ambient_temp_c = parseFloat(ambient);
      if (fuel) payload.fuel_inr_per_kg = parseFloat(fuel);
      if (elec) payload.electricity_inr_per_kwh = parseFloat(elec);
      const res = await endpoints.runScenario(payload);
      setResult(res);
    } finally {
      setRunning(false);
    }
  };

  const inject = async (type, sev) => { await endpoints.injectAnomaly(type, sev); refresh(); };
  const clearAll = async () => { await endpoints.clearAnomaly(null); refresh(); };

  if (!cfg) return <Loading />;
  const injected = cfg.injected || [];

  return (
    <div data-testid="simulation-studio-page">
      <PageHeader icon={FlaskConical} title="Simulation Studio"
        subtitle="Run the Digital Plant — what-if scenarios and anomaly injection, all without touching production." />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
        <Panel testid="panel-scenario" title="What-If Scenario" subtitle="Project the dairy under different conditions">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <Field label="Milk Throughput" unit="KLPD" value={milk} onChange={setMilk} placeholder={cfg.config.design_klpd} />
            <Field label="Ambient Temp" unit="°C" value={ambient} onChange={setAmbient} placeholder="35" />
            <Field label="Fuel Cost" unit="₹/kg" value={fuel} onChange={setFuel} placeholder={cfg.config.tariffs.fuel_inr_per_kg} />
            <Field label="Power Tariff" unit="₹/kWh" value={elec} onChange={setElec} placeholder={cfg.config.tariffs.electricity_inr_per_kwh} />
          </div>
          <button onClick={run} disabled={running} data-testid="run-scenario-btn"
            className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white py-2.5 rounded-md text-sm font-semibold hover:bg-slate-700 disabled:opacity-60 transition-colors">
            <Play size={15} className={running ? "animate-pulse" : ""} /> {running ? "Simulating…" : "Run Simulation"}
          </button>

          {result && (
            <div className="mt-4" data-testid="scenario-result">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Projected vs Baseline</div>
              <Delta label="Utility Cost ₹/L" base={result.baseline.utility_cost_l} proj={result.projected.enterprise.utility_cost_l} fmt={(v) => inrL(v)} />
              <Delta label="Margin ₹/L" base={result.baseline.margin_l} proj={result.projected.enterprise.margin_l} fmt={(v) => inrL(v)} lower={false} />
              <Delta label="Production KLPD" base={result.baseline.production_klpd} proj={result.projected.enterprise.production_klpd} fmt={(v) => num(v, 0)} lower={false} />
              <Delta label="Carbon kgCO₂/L" base={result.baseline.carbon_l} proj={result.projected.enterprise.carbon_l} fmt={(v) => num(v, 3)} />
            </div>
          )}
        </Panel>

        <Panel testid="panel-anomaly-injection" title="Anomaly Injection Studio" subtitle="Training instance only — inject failures & watch twins react"
          right={injected.length > 0 && (
            <button onClick={clearAll} data-testid="clear-anomalies-btn" className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-semibold">
              <Trash2 size={13} /> Clear all
            </button>
          )}>
          <div className="space-y-2">
            {Object.entries(cfg.anomaly_catalog).map(([key, a]) => {
              const active = injected.find((x) => x.type === key);
              return (
                <div key={key} className={`flex items-center justify-between border rounded-md p-2.5 ${active ? "border-red-300 bg-red-50" : "border-slate-200"}`} data-testid={`anomaly-${key}`}>
                  <div className="flex items-center gap-2">
                    <Bug size={14} className={active ? "text-red-500" : "text-slate-400"} />
                    <div>
                      <div className="text-sm font-medium text-slate-800">{a.label}</div>
                      <div className="text-[11px] text-slate-400 capitalize">{a.twin}{active ? ` · injected (${active.severity})` : ""}</div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {["low", "medium", "high"].map((sev) => (
                      <button key={sev} onClick={() => inject(key, sev)} data-testid={`inject-${key}-${sev}`}
                        className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-slate-100 hover:bg-slate-900 hover:text-white transition-colors">
                        {sev[0]}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-[11px] text-slate-400">
            Inject an anomaly, then open the relevant twin or the Enterprise Command Center to see the physics deviation, ₹ impact and AI root-cause appear live.
          </p>
        </Panel>
      </div>
    </div>
  );
}
